/**
 * keeperSprite.ts — richer goalkeeper rendering, factored out of render.ts.
 *
 * Inputs are pre-projected screen points (feet, hip, shoulder, head,
 * lead-glove, trail-glove) plus a screen-scale `bodyPx`. The keeper's
 * physics-defined catch centre is `lead`; this file does not touch the
 * physics math.
 *
 * Visual upgrade vs. v1 stick figure:
 *   - Jersey gradient + chest accent stripe (Curvle yellow)
 *   - Shorts with a thigh shadow
 *   - Hair cap on top of the head; face highlight
 *   - Detailed gloves with a finger-line groove
 *   - Wrist cuff bands at the glove ↔ arm join
 *   - Dive motion lines behind the lead glove
 *
 * Anchors are unchanged so physics stays in sync.
 */

interface ScreenPoint {
  x: number;
  y: number;
  visible: boolean;
}

export interface KeeperSpriteOpts {
  feet: ScreenPoint;
  hip: ScreenPoint;
  shoulder: ScreenPoint;
  head: ScreenPoint;
  /** Lead glove — the physics catch centre. */
  lead: ScreenPoint;
  /** Opposite (trailing) glove. */
  trail: ScreenPoint;
  /** rest-pose lead projection — used to draw the motion swoosh. */
  restLead: ScreenPoint;
  /** Pre-computed screen-pixel scale: max(8, 0.55 * focal / z). */
  bodyPx: number;
  /** Dive progress 0..1 (already smoothstep'd by caller). */
  dive: number;
  /** Soft shadow projection point (already perspective-projected). */
  shadow: ScreenPoint;
}

const JERSEY = "#fde047";       // Curvle amber-300
const JERSEY_DARK = "#ca8a04";  // amber-600 — shadow side
const SHORTS = "#1f2937";       // slate-800
const SHORTS_DARK = "#0f172a";  // slate-900
const SKIN = "#f5d0a9";
const HAIR = "#1f1208";
const GLOVE = "#22d3ee";        // cyan-400
const GLOVE_DARK = "#0e7490";   // cyan-700
const CUFF = "#0c4a6e";         // cyan-900

/** Tapered limb between two screen points. Fills `fill`, then strokes a
 *  thin dark outline so the body silhouette stays readable on any pitch
 *  background. */
function drawLimb(
  ctx: CanvasRenderingContext2D,
  a: ScreenPoint,
  b: ScreenPoint,
  widthA: number,
  widthB: number,
  fill: string,
): void {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const len = Math.max(1, Math.sqrt(dx * dx + dy * dy));
  const px = -dy / len;
  const py = dx / len;
  ctx.beginPath();
  ctx.moveTo(a.x + px * widthA, a.y + py * widthA);
  ctx.lineTo(a.x - px * widthA, a.y - py * widthA);
  ctx.lineTo(b.x - px * widthB, b.y - py * widthB);
  ctx.lineTo(b.x + px * widthB, b.y + py * widthB);
  ctx.closePath();
  ctx.fillStyle = fill;
  ctx.fill();
  ctx.strokeStyle = "rgba(0,0,0,0.55)";
  ctx.lineWidth = 1;
  ctx.stroke();
}

/** Stylised goalkeeper glove — slightly larger circle than the lead-glove
 *  hit-target, with a finger-line groove and a wrist cuff band. */
function drawGlove(
  ctx: CanvasRenderingContext2D,
  glove: ScreenPoint,
  joint: ScreenPoint,
  radius: number,
): void {
  // Wrist cuff — short band oriented perpendicular to the arm axis.
  const dx = glove.x - joint.x;
  const dy = glove.y - joint.y;
  const len = Math.max(1, Math.sqrt(dx * dx + dy * dy));
  const ux = dx / len;
  const uy = dy / len;
  // Pull the cuff back from the glove centre so it sits at the wrist.
  const cuffCx = glove.x - ux * radius * 0.92;
  const cuffCy = glove.y - uy * radius * 0.92;
  const cuffH = radius * 0.36;
  const cuffW = radius * 1.05;
  ctx.save();
  ctx.translate(cuffCx, cuffCy);
  ctx.rotate(Math.atan2(uy, ux));
  ctx.fillStyle = CUFF;
  ctx.fillRect(-cuffH / 2, -cuffW / 2, cuffH, cuffW);
  ctx.restore();

  // Main glove ball.
  ctx.fillStyle = GLOVE;
  ctx.beginPath();
  ctx.arc(glove.x, glove.y, radius, 0, Math.PI * 2);
  ctx.fill();
  ctx.strokeStyle = GLOVE_DARK;
  ctx.lineWidth = 1.5;
  ctx.stroke();

  // Finger-line groove across the glove centre, perpendicular to arm axis.
  ctx.strokeStyle = "rgba(12, 74, 110, 0.85)";
  ctx.lineWidth = Math.max(1, radius * 0.12);
  ctx.lineCap = "round";
  const grooveLen = radius * 0.92;
  // Perpendicular to arm direction.
  const px = -uy;
  const py = ux;
  ctx.beginPath();
  ctx.moveTo(glove.x - px * grooveLen, glove.y - py * grooveLen);
  ctx.lineTo(glove.x + px * grooveLen, glove.y + py * grooveLen);
  ctx.stroke();

  // Small highlight crescent for glossiness.
  ctx.fillStyle = "rgba(255,255,255,0.45)";
  ctx.beginPath();
  ctx.arc(glove.x - radius * 0.32, glove.y - radius * 0.32, radius * 0.32, 0, Math.PI * 2);
  ctx.fill();
}

/** Draw the entire keeper sprite. Caller must have already done the
 *  visibility check (all anchors visible). */
export function drawKeeperSprite(
  ctx: CanvasRenderingContext2D,
  opts: KeeperSpriteOpts,
): void {
  const { feet, hip, shoulder, head, lead, trail, restLead, bodyPx, dive, shadow } = opts;

  // ----- ground shadow -----
  if (shadow.visible) {
    const shadowAlpha = 0.5 * (1 - 0.4 * dive);
    ctx.fillStyle = `rgba(0,0,0,${shadowAlpha})`;
    ctx.beginPath();
    ctx.ellipse(
      shadow.x,
      shadow.y,
      bodyPx * (0.85 + 0.4 * dive),
      bodyPx * 0.22,
      0,
      0,
      Math.PI * 2,
    );
    ctx.fill();
  }

  // ----- legs (shorts), shaded side + main panel -----
  drawLimb(ctx, feet, hip, bodyPx * 0.42, bodyPx * 0.40, SHORTS);
  // Thigh shadow band (just the right edge to suggest light from upper-left).
  const legDx = hip.x - feet.x;
  const legDy = hip.y - feet.y;
  const legLen = Math.max(1, Math.sqrt(legDx * legDx + legDy * legDy));
  const lpx = -legDy / legLen;
  const lpy = legDx / legLen;
  ctx.fillStyle = SHORTS_DARK;
  ctx.beginPath();
  ctx.moveTo(feet.x + lpx * bodyPx * 0.32, feet.y + lpy * bodyPx * 0.32);
  ctx.lineTo(feet.x + lpx * bodyPx * 0.42, feet.y + lpy * bodyPx * 0.42);
  ctx.lineTo(hip.x + lpx * bodyPx * 0.40, hip.y + lpy * bodyPx * 0.40);
  ctx.lineTo(hip.x + lpx * bodyPx * 0.28, hip.y + lpy * bodyPx * 0.28);
  ctx.closePath();
  ctx.fill();

  // ----- torso (jersey), main fill + dark side + chest accent -----
  drawLimb(ctx, hip, shoulder, bodyPx * 0.40, bodyPx * 0.48, JERSEY);
  // Jersey shadow (right side).
  const torsoDx = shoulder.x - hip.x;
  const torsoDy = shoulder.y - hip.y;
  const torsoLen = Math.max(1, Math.sqrt(torsoDx * torsoDx + torsoDy * torsoDy));
  const tpx = -torsoDy / torsoLen;
  const tpy = torsoDx / torsoLen;
  ctx.fillStyle = JERSEY_DARK;
  ctx.beginPath();
  ctx.moveTo(hip.x + tpx * bodyPx * 0.28, hip.y + tpy * bodyPx * 0.28);
  ctx.lineTo(hip.x + tpx * bodyPx * 0.40, hip.y + tpy * bodyPx * 0.40);
  ctx.lineTo(shoulder.x + tpx * bodyPx * 0.48, shoulder.y + tpy * bodyPx * 0.48);
  ctx.lineTo(shoulder.x + tpx * bodyPx * 0.32, shoulder.y + tpy * bodyPx * 0.32);
  ctx.closePath();
  ctx.fill();
  // Chest accent stripe across the jersey at ~mid-torso.
  const midX = hip.x + torsoDx * 0.55;
  const midY = hip.y + torsoDy * 0.55;
  ctx.strokeStyle = "rgba(31,41,55,0.75)";
  ctx.lineWidth = Math.max(1.5, bodyPx * 0.10);
  ctx.lineCap = "round";
  ctx.beginPath();
  ctx.moveTo(midX + tpx * bodyPx * 0.32, midY + tpy * bodyPx * 0.32);
  ctx.lineTo(midX - tpx * bodyPx * 0.32, midY - tpy * bodyPx * 0.32);
  ctx.stroke();

  // ----- trail arm -----
  ctx.lineCap = "round";
  ctx.strokeStyle = JERSEY;
  ctx.lineWidth = Math.max(3, bodyPx * 0.28);
  ctx.beginPath();
  ctx.moveTo(shoulder.x, shoulder.y);
  ctx.lineTo(trail.x, trail.y);
  ctx.stroke();
  ctx.strokeStyle = "rgba(0,0,0,0.55)";
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(shoulder.x, shoulder.y);
  ctx.lineTo(trail.x, trail.y);
  ctx.stroke();

  // ----- lead arm (the focus) -----
  ctx.strokeStyle = JERSEY;
  ctx.lineWidth = Math.max(4, bodyPx * 0.32);
  ctx.beginPath();
  ctx.moveTo(shoulder.x, shoulder.y);
  ctx.lineTo(lead.x, lead.y);
  ctx.stroke();
  ctx.strokeStyle = "rgba(0,0,0,0.55)";
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(shoulder.x, shoulder.y);
  ctx.lineTo(lead.x, lead.y);
  ctx.stroke();

  // ----- neck stub -----
  ctx.fillStyle = SKIN;
  const neckW = bodyPx * 0.22;
  const neckH = bodyPx * 0.16;
  ctx.fillRect(shoulder.x - neckW / 2, shoulder.y - neckH, neckW, neckH);

  // ----- head -----
  const headR = Math.max(5, bodyPx * 0.42);
  ctx.fillStyle = SKIN;
  ctx.beginPath();
  ctx.arc(head.x, head.y, headR, 0, Math.PI * 2);
  ctx.fill();
  ctx.strokeStyle = "rgba(0,0,0,0.5)";
  ctx.lineWidth = 1;
  ctx.stroke();
  // Hair cap on the upper-rear of the head (oriented relative to body axis
  // so it stays correct as the keeper leans into the dive).
  const bodyDx = head.x - hip.x;
  const bodyDy = head.y - hip.y;
  const bodyAng = Math.atan2(bodyDy, bodyDx);
  ctx.save();
  ctx.translate(head.x, head.y);
  ctx.rotate(bodyAng + Math.PI / 2);
  ctx.fillStyle = HAIR;
  ctx.beginPath();
  ctx.arc(0, 0, headR, Math.PI * 1.05, Math.PI * 1.95, false);
  ctx.lineTo(headR * 0.95, -headR * 0.10);
  ctx.lineTo(-headR * 0.95, -headR * 0.10);
  ctx.closePath();
  ctx.fill();
  ctx.restore();
  // Cheek highlight for a face without drawing features.
  ctx.fillStyle = "rgba(255,255,255,0.22)";
  ctx.beginPath();
  ctx.arc(head.x - headR * 0.20, head.y + headR * 0.22, headR * 0.24, 0, Math.PI * 2);
  ctx.fill();

  // ----- gloves -----
  const gloveR = Math.max(5, bodyPx * 0.36);
  drawGlove(ctx, lead, shoulder, gloveR);
  drawGlove(ctx, trail, shoulder, gloveR * 0.92);

  // ----- motion trail behind lead glove during dive -----
  if (dive > 0.05 && dive < 1 && restLead.visible) {
    const tdx = lead.x - restLead.x;
    const tdy = lead.y - restLead.y;
    ctx.strokeStyle = "rgba(34,211,238,0.35)";
    ctx.lineWidth = Math.max(2, bodyPx * 0.45);
    ctx.lineCap = "round";
    ctx.beginPath();
    ctx.moveTo(lead.x - tdx * 0.25, lead.y - tdy * 0.25);
    ctx.lineTo(lead.x - tdx * 0.55, lead.y - tdy * 0.55);
    ctx.stroke();
    // Faster speed lines.
    ctx.strokeStyle = "rgba(255,255,255,0.4)";
    ctx.lineWidth = Math.max(1, bodyPx * 0.10);
    ctx.beginPath();
    ctx.moveTo(lead.x - tdx * 0.42, lead.y - tdy * 0.42);
    ctx.lineTo(lead.x - tdx * 0.78, lead.y - tdy * 0.78);
    ctx.stroke();
  }
}
